import type { HelpGuide } from '../types';

export const coaGuide: HelpGuide = {
  key: 'coa',
  title: 'ผังบัญชี (Chart of Accounts)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ ผังบัญชี',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/coa/คู่มือการใช้งาน_ผังบัญชี_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ผังบัญชี (Chart of Accounts)” คือทะเบียนรหัสบัญชีหลักของบริษัท ใช้ดูและจัดการบัญชีแยกประเภททั้งหมด เช่น เพิ่มบัญชีใหม่ แก้ไขชื่อ/การจัดกลุ่มสำหรับงบการเงิน ตั้งนโยบายมิติ (สาขา/หน่วยงาน) และนำเข้า/ส่งออกผังบัญชีเป็นไฟล์ Excel/CSV ผังบัญชีนี้เป็นค่าตั้งต้นที่ใบสำคัญและรายงานทุกตัวอ้างอิงถึง',
        },
        {
          kind: 'callout',
          text: 'รหัสบัญชีเป็นเลข 8 หลัก และ “ประเภทบัญชี” อิงจากเลขหลักแรกของรหัสโดยอัตโนมัติ: 1 = สินทรัพย์ · 2 = หนี้สิน · 3 = ส่วนของผู้ถือหุ้น · 4 = รายได้ · 5 = ต้นทุน · 6 = ค่าใช้จ่าย ทำให้ไม่ต้องเลือกประเภทเอง',
        },
        { kind: 'para', text: 'สิ่งที่ทำได้บนหน้านี้:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ดูผังบัญชีทั้งหมด — ',
              text: 'การ์ดสรุปจำนวนบัญชี 6 ประเภท + ตารางรายการบัญชี',
            },
            { lead: 'กรอง/ค้นหา — ', text: 'กรองตามประเภทบัญชี หรือค้นหาด้วยรหัส/ชื่อบัญชี' },
            {
              lead: 'เพิ่ม/แก้ไขบัญชี — ',
              text: 'สร้างบัญชีใหม่ หรือแก้ไขชื่อ/กลุ่ม/นโยบายของบัญชีเดิม',
            },
            {
              lead: 'ปิดใช้งานบัญชี — ',
              text: 'ปิดบัญชีที่ไม่ใช้แล้ว (ไม่ลบทิ้ง เพราะผูกกับใบสำคัญ)',
            },
            {
              lead: 'นำเข้า/ส่งออก — ',
              text: 'อัปโหลดผังบัญชีจำนวนมากจากไฟล์ หรือดาวน์โหลดทั้งผัง',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/coa/01-overview.png',
          caption: 'ภาพรวมผังบัญชี — การ์ดสรุปจำนวนบัญชี 6 ประเภท และตารางบัญชีทั้งหมด',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'การ์ดสรุปจำนวนบัญชี (KPI) 6 ใบ — นับจำนวนบัญชีแยกตามประเภท:' },
        {
          kind: 'bullets',
          items: [
            { lead: 'สินทรัพย์ — ', text: 'จำนวนบัญชีประเภทสินทรัพย์ (รหัสขึ้นต้น 1)' },
            { lead: 'หนี้สิน — ', text: 'จำนวนบัญชีประเภทหนี้สิน (รหัสขึ้นต้น 2)' },
            { lead: 'ส่วนของผู้ถือหุ้น — ', text: 'จำนวนบัญชีส่วนของผู้ถือหุ้น (รหัสขึ้นต้น 3)' },
            { lead: 'รายได้ — ', text: 'จำนวนบัญชีประเภทรายได้ (รหัสขึ้นต้น 4)' },
            { lead: 'ต้นทุน — ', text: 'จำนวนบัญชีประเภทต้นทุน (รหัสขึ้นต้น 5)' },
            { lead: 'ค่าใช้จ่าย — ', text: 'จำนวนบัญชีประเภทค่าใช้จ่าย (รหัสขึ้นต้น 6)' },
          ],
        },
        {
          kind: 'para',
          text: 'ปุ่มมุมขวาบน: นำเข้า · ส่งออก · เพิ่มบัญชี (ปุ่มสีแดง) แถวตัวกรองเหนือตาราง: ช่องค้นหา “ค้นหารหัสบัญชีหรือชื่อบัญชี” · ตัวเลือก “ประเภท: ทั้งหมด” · ปุ่ม “ล้างการค้นหา”',
        },
        {
          kind: 'para',
          text: 'ตารางผังบัญชีมี 11 คอลัมน์: รหัสบัญชี · ชื่อบัญชี · ประเภท · กลุ่มใหญ่ · กลุ่มกลาง · กลุ่มย่อย · กลุ่มในงบการเงิน · สาขา · หน่วยงาน / ศูนย์ต้นทุน · สถานะ · จัดการ (ปุ่ม แก้ไข / ปิดใช้งาน) ใต้ตารางแสดงจำนวน “แสดง X จาก Y บัญชี”',
        },
      ],
    },
    {
      id: 'filter-type',
      heading: '3. การกรองตามประเภทบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'ที่ตัวเลือก “ประเภท: ทั้งหมด” เลือกประเภทที่ต้องการ เช่น “สินทรัพย์” ตารางจะแสดงเฉพาะบัญชีประเภทนั้น ช่วยให้หาบัญชีในหมวดเดียวกันได้เร็วขึ้น เมื่อต้องการกลับมาดูทุกประเภทให้กด “ล้างการค้นหา” หรือเลือก “ทั้งหมด” อีกครั้ง',
        },
        {
          kind: 'image',
          src: '/help/coa/02-filter-type.png',
          caption: 'กรองตามประเภท “สินทรัพย์” — ตารางแสดงเฉพาะบัญชีสินทรัพย์',
        },
      ],
    },
    {
      id: 'search',
      heading: '4. การค้นหารหัส/ชื่อบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'พิมพ์คำค้นในช่อง “ค้นหารหัสบัญชีหรือชื่อบัญชี” ระบบจะกรองทันทีตามทั้งรหัสบัญชีและชื่อบัญชีภาษาไทย เช่น พิมพ์ “เงิน” จะแสดงเฉพาะบัญชีที่มีคำว่า “เงิน” ในชื่อ จำนวนใต้ตารางจะอัปเดตเป็นจำนวนที่ตรงเงื่อนไข ใช้ค้นหาร่วมกับตัวกรองประเภทได้',
        },
        {
          kind: 'image',
          src: '/help/coa/03-search.png',
          caption: 'ค้นหา “เงิน” — แสดงเฉพาะบัญชีที่ชื่อหรือรหัสตรงกับคำค้น',
        },
      ],
    },
    {
      id: 'add',
      heading: '5. การเพิ่มบัญชีใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม เพิ่มบัญชี จะเปิดหน้าต่าง “เพิ่มบัญชีใหม่” กรอก รหัสบัญชี (เลข 8 หลัก) — เมื่อพิมพ์รหัส ระบบจะแสดง “ประเภท” ให้อัตโนมัติจากเลขหลักแรก แล้วกรอก ชื่อบัญชี (ไทย) ส่วน ชื่อบัญชี (อังกฤษ) เว้นว่างได้ (จะใช้ชื่อไทยแทน)',
        },
        {
          kind: 'para',
          text: 'ในการ์ด “การจัดกลุ่มบัญชี (สำหรับงบการเงิน)” เลือกกลุ่มใหญ่ → กลุ่มกลาง → กลุ่มย่อย → กลุ่มในงบการเงิน (จะเลือกจากรายการเดิมหรือพิมพ์ค่าใหม่ก็ได้) และตั้ง นโยบายสาขา / นโยบายหน่วยงาน / ศูนย์ต้นทุน (ไม่ใช้ / ไม่บังคับ / บังคับ) พร้อมเครื่องหมาย ใช้งาน เสร็จแล้วกด + เพิ่มบัญชี',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'นโยบายมิติคุมการลงบัญชี: ถ้าตั้งเป็น “บังคับ” ทุกบรรทัดในใบสำคัญที่ลงบัญชีนี้ ต้องระบุสาขา/หน่วยงานเสมอ ปกติบัญชีกำไรขาดทุนมักตั้งหน่วยงานเป็น “บังคับ”',
        },
        {
          kind: 'image',
          src: '/help/coa/04-add-modal.png',
          caption: 'เพิ่มบัญชีใหม่ — รหัส 8 หลักกำหนดประเภทบัญชีให้อัตโนมัติ',
        },
      ],
    },
    {
      id: 'edit',
      heading: '6. การแก้ไขบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม แก้ไข ที่แถวของบัญชี จะเปิดหน้าต่าง “แก้ไขบัญชี” พร้อมค่าปัจจุบัน แก้ไขได้: ชื่อบัญชี (ไทย/อังกฤษ) · การจัดกลุ่มสำหรับงบการเงิน · นโยบายสาขา/หน่วยงาน · สถานะใช้งาน เสร็จแล้วกด บันทึกการแก้ไข',
        },
        {
          kind: 'callout',
          variant: 'warn',
          text: 'รหัสบัญชีแก้ไม่ได้ เพราะผูกกับบรรทัดในใบสำคัญ (journal_lines) ที่ลงไปแล้ว และประเภทบัญชีก็เปลี่ยนไม่ได้ (อิงจากรหัส) หากต้องการเปลี่ยนรหัส ให้ปิดใช้งานบัญชีเดิมแล้วสร้างบัญชีใหม่แทน',
        },
        {
          kind: 'image',
          src: '/help/coa/05-edit-modal.png',
          caption: 'แก้ไขบัญชี — รหัสแก้ไม่ได้ แต่แก้ชื่อ/กลุ่ม/นโยบายได้',
        },
      ],
    },
    {
      id: 'group-cascade',
      heading: '7. การจัดกลุ่มบัญชีแบบลำดับชั้น',
      blocks: [
        {
          kind: 'para',
          text: 'การจัดกลุ่มสำหรับงบการเงินเป็นลำดับชั้น 4 ระดับ: กลุ่มใหญ่ → กลุ่มกลาง → กลุ่มย่อย → กลุ่มในงบการเงิน เมื่อเลือกกลุ่มใหญ่แล้ว ช่องกลุ่มกลางจึงเปิดให้เลือก และจะแสดงเฉพาะตัวเลือกที่อยู่ใต้กลุ่มใหญ่นั้น ไล่ลงไปตามลำดับ ถ้ากลุ่มในงบการเงินมีตัวเลือกเดียว ระบบจะเติมให้อัตโนมัติ',
        },
        {
          kind: 'para',
          text: 'หากเปลี่ยนกลุ่มระดับบน ช่องระดับล่างจะถูกล้างค่าเพื่อให้เลือกใหม่ให้สอดคล้องกัน การจัดกลุ่มนี้ใช้จัดเรียงบัญชีในงบแสดงฐานะการเงิน/งบกำไรขาดทุนให้ถูกบรรทัด',
        },
        {
          kind: 'image',
          src: '/help/coa/06-group-cascade.png',
          caption: 'เลือกกลุ่มบัญชีแบบลำดับชั้น — ใหญ่ → กลาง → ย่อย → กลุ่มในงบการเงิน',
        },
      ],
    },
    {
      id: 'import-pick',
      heading: '8. การนำเข้าผังบัญชี — เลือกไฟล์',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม นำเข้า จะเปิดหน้าต่าง “นำเข้าผังบัญชี (CSV)” เลือกไฟล์ Excel (.xlsx) หรือ CSV ที่มีหัวคอลัมน์ตามที่กำหนด รหัสบัญชีที่มีอยู่แล้วจะถูก “อัปเดต” ส่วนรหัสใหม่จะถูก “สร้าง” แนะนำให้กด “ดาวน์โหลดเทมเพลต” หรือใช้ปุ่ม “ส่งออก” แล้วแก้ไขในไฟล์นั้นเพื่อความถูกต้อง',
        },
        {
          kind: 'para',
          text: 'ตารางในหน้าต่างอธิบายคอลัมน์ที่ใช้ได้ (คอลัมน์ / ความหมาย / จำเป็น / ค่าที่ใช้ได้):',
        },
        {
          kind: 'table',
          head: ['คอลัมน์', 'ความหมาย', 'จำเป็น', 'ค่าที่ใช้ได้'],
          rows: [
            ['code', 'รหัสบัญชี', 'จำเป็น', 'เช่น 1111 (2–20 ตัวอักษร, ห้ามซ้ำในบริษัท)'],
            ['nameTh', 'ชื่อบัญชี (ไทย)', 'จำเป็น', 'เช่น เงินสดในมือ'],
            ['nameEn', 'ชื่อบัญชี (อังกฤษ)', '-', 'เว้นว่างได้ — จะใช้ชื่อไทยแทน'],
            ['type', 'ประเภทบัญชี', 'จำเป็น', 'ASSET/LIABILITY/EQUITY/REVENUE/COST/EXPENSE'],
            ['level', 'ระดับ', 'จำเป็น', '0–6'],
            ['parentCode', 'รหัสบัญชีแม่', '-', 'เว้นว่างได้'],
            ['isControl', 'บัญชีคุม', '-', 'true/false'],
            ['isActive', 'สถานะใช้งาน', '-', 'true/false'],
            ['branchPolicy', 'นโยบายสาขา', '-', 'NONE/OPTIONAL/REQUIRED'],
            ['departmentPolicy', 'นโยบายหน่วยงาน / ศูนย์ต้นทุน', '-', 'NONE/OPTIONAL/REQUIRED'],
          ],
        },
        {
          kind: 'image',
          src: '/help/coa/07-import-pick.png',
          caption: 'นำเข้าผังบัญชี ขั้นเลือกไฟล์ — พร้อมตารางอธิบายคอลัมน์',
        },
      ],
    },
    {
      id: 'import-preview',
      heading: '9. การนำเข้าผังบัญชี — ตัวอย่างก่อนนำเข้า',
      blocks: [
        {
          kind: 'para',
          text: 'หลังเลือกไฟล์ ระบบจะอ่านไฟล์และแสดงตัวอย่างก่อนนำเข้า: ชื่อไฟล์ + จำนวนแถวที่พบ และสรุปยอด สร้างใหม่ (เขียว) / อัปเดต (น้ำเงิน) / ผิดพลาด (แดง) ตารางรายการที่ถูกต้องจะติดป้าย “สร้างใหม่” หรือ “อัปเดต” ส่วนแถวที่ผิดจะแสดงข้อความว่าผิดที่แถวไหนและเพราะอะไร',
        },
        {
          kind: 'callout',
          variant: 'info',
          text: 'ตรวจสอบยอดสรุปและแถวที่ผิดพลาดก่อนเสมอ ถ้ามีคอลัมน์จำเป็นหาย ระบบจะแจ้ง “ไม่พบคอลัมน์ที่จำเป็น” เมื่อพร้อมแล้วกด “นำเข้า X รายการ” (ปุ่มจะกดได้เมื่อมีรายการที่ถูกต้องอย่างน้อย 1 แถว)',
        },
        {
          kind: 'image',
          src: '/help/coa/08-import-preview.png',
          caption: 'ตัวอย่างก่อนนำเข้า — สรุปยอด สร้างใหม่ / อัปเดต / ผิดพลาด',
        },
      ],
    },
    {
      id: 'import-done',
      heading: '10. การนำเข้าผังบัญชี — สรุปผล',
      blocks: [
        {
          kind: 'para',
          text: 'เมื่อกดนำเข้า ระบบจะประมวลผลทีละรายการ (แสดงความคืบหน้า “กำลังนำเข้า… X/Y”) เสร็จแล้วจะสรุปผล สร้าง (เขียว) / อัปเดต (น้ำเงิน) / ล้มเหลว (แดง) หากมีรายการล้มเหลวจะแสดงรหัสบัญชีพร้อมสาเหตุ ให้แก้ไขในไฟล์แล้วนำเข้าใหม่เฉพาะส่วนนั้น เมื่อตรวจดูเรียบร้อยกด “เสร็จสิ้น” เพื่อปิดหน้าต่าง',
        },
        {
          kind: 'image',
          src: '/help/coa/09-import-done.png',
          caption: 'สรุปผลนำเข้าเสร็จสิ้น — จำนวนที่สร้าง / อัปเดต / ล้มเหลว',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '11. สรุปสถานะและป้ายกำกับ',
      blocks: [
        { kind: 'para', text: 'สถานะบัญชี (คอลัมน์ สถานะ):' },
        {
          kind: 'table',
          head: ['ป้าย', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'บัญชีที่เปิดใช้งานอยู่ (จุดสีเขียว)'],
            ['ปิด', 'บัญชีที่ถูกปิดใช้งานแล้ว (ปิดใช้งาน ไม่ลบทิ้ง)'],
          ],
        },
        { kind: 'para', text: 'ป้ายนโยบายมิติ (คอลัมน์ สาขา และ หน่วยงาน / ศูนย์ต้นทุน):' },
        {
          kind: 'table',
          head: ['ป้าย', 'ความหมาย'],
          rows: [
            ['บังคับ', 'ทุกบรรทัดที่ลงบัญชีนี้ต้องระบุมิติ (สาขา/หน่วยงาน) เสมอ'],
            ['ไม่บังคับ', 'จะระบุมิติหรือไม่ก็ได้ (ค่าตั้งต้น)'],
            ['ไม่ใช้', 'บัญชีนี้ไม่ใช้มิติดังกล่าว'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมแก้ “รหัสบัญชี” ไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'รหัสบัญชีผูกกับบรรทัดในใบสำคัญที่ลงไปแล้ว จึงล็อกไว้ ถ้าต้องเปลี่ยนรหัส ให้ปิดบัญชีเดิมแล้วสร้างใหม่',
            },
          ],
        },
        { kind: 'para', text: 'ทำไมเลือก “ประเภท” เองไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ประเภทบัญชีอิงจากเลขหลักแรกของรหัสโดยอัตโนมัติ (1=สินทรัพย์ … 6=ค่าใช้จ่าย) จึงไม่ต้องเลือกเอง',
            },
          ],
        },
        { kind: 'para', text: 'ปิดบัญชีแล้วหายไปจากตารางไหม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ไม่หาย — การปิดใช้งานเป็นการตั้งสถานะเป็น “ปิด” (soft-delete) ไม่ได้ลบทิ้ง เพื่อรักษาความสัมพันธ์กับใบสำคัญเดิม',
            },
          ],
        },
        { kind: 'para', text: 'นำเข้าไฟล์แล้วบางแถวล้มเหลวต้องทำอย่างไร?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ดูสาเหตุที่ระบบแจ้งต่อท้ายรหัสบัญชี แก้ไขในไฟล์ต้นฉบับ แล้วนำเข้าใหม่ รหัสที่มีอยู่แล้วจะถูกอัปเดตทับ ไม่สร้างซ้ำ',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'ผังบัญชี (Chart of Accounts) — ',
              text: 'ทะเบียนรหัสบัญชีแยกประเภททั้งหมดของบริษัท',
            },
            {
              lead: 'ประเภทบัญชี — ',
              text: 'หมวดบัญชี 6 ประเภท อิงจากเลขหลักแรกของรหัส (สินทรัพย์/หนี้สิน/ส่วนของผู้ถือหุ้น/รายได้/ต้นทุน/ค่าใช้จ่าย)',
            },
            {
              lead: 'การจัดกลุ่มสำหรับงบการเงิน — ',
              text: 'ลำดับชั้น กลุ่มใหญ่/กลุ่มกลาง/กลุ่มย่อย/กลุ่มในงบการเงิน ใช้จัดเรียงบัญชีในงบ',
            },
            {
              lead: 'นโยบายมิติ (สาขา/หน่วยงาน) — ',
              text: 'กำหนดว่าการลงบัญชีนี้ต้องระบุสาขา/หน่วยงานหรือไม่ (ไม่ใช้/ไม่บังคับ/บังคับ)',
            },
            {
              lead: 'บัญชีคุม (Control account) — ',
              text: 'บัญชีที่ควบคุมยอดรวมของกลุ่มย่อย (กำหนดผ่านการนำเข้า)',
            },
            {
              lead: 'ปิดใช้งาน (Soft-delete) — ',
              text: 'การตั้งสถานะบัญชีเป็น “ปิด” โดยไม่ลบข้อมูลทิ้ง',
            },
          ],
        },
      ],
    },
  ],
};
